import { Add, ExpandLess, ExpandMore, FilterList, LockOutlined, Star, StarOutline } from '@mui/icons-material'
import {
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  ListItem,
  Stack,
  SvgIcon,
  TableCell,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'
import FilterProjectsMenu from '../../components/FilterMenu/Projects/FilterProjectsMenu'
import Searchbar from '../../components/Searchbar/Searchbar'
import navigation from '../../config/navigation'
import { useAppDispatch } from '../../store/redux/hooks'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { StyledPageContent } from '../../styles/PageStyles'
import GenerateReportPopup from '../../components/Popup/GenerateReportPopup'
import ReportType from '../../store/types/enums/ReportType'
import ReportPopupData from '../../store/types/utils/ReportPopupUtil'
import DataTable from '../../components/Table/DataTable'
import { useGetProjectsQuery, useSetIsProjectFavoriteForUserMutation } from '../../store/redux/reducers/projectsSlice'
import { Project, RequestProps } from '../../store/types'
import defaultProps from '../../config/defaultProps'
import ProjectActionMenu from '../../components/Popup/ProjectActionMenu'
import { useSelector } from 'react-redux'
import { StickyCell } from '../../styles/WorklogTableStyles'
import AvatarComponent from '../../components/avatar/AvatarComponent'
import ProjectStatus from '../../store/types/enums/ProjectStatus'
import LocalStorageTypes from '../../store/types/enums/LocalStorageTypes'
import SessionStorageKeys from '../../store/types/enums/SessionStorageKeys'
import usePersistedPage from '../../store/utils/usePersistedPage'
import {
  getCurrentUser,
  getIsManagementOrAdministrator,
  getIsProjectLeader,
} from '../../store/redux/reducers/currentUserSlice'
import { FullName } from '../../store/types/utils/DataTableUtils'
import HeaderCountLabel from '../../components/Page/HeaderCountLabel'
import { SquareButton } from '../../styles/ButtonStyles'
import { RootState } from '../../store/redux/store'

interface Column {
  id: string
  label: string
}

interface Row {
  id: string

  [key: string]: any
}

const ProjectsView = () => {
  const theme = useTheme()
  const currentUser = useSelector(getCurrentUser)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [selectedData, setSelectedData] = useState<ReportPopupData[]>([])
  const [page, setPage] = usePersistedPage(SessionStorageKeys.ProjectsTablePage)
  const [requestProps, setRequestProps] = useState<RequestProps>({ ...defaultProps })
  const [setIsProjectFavoriteForUser] = useSetIsProjectFavoriteForUserMutation()
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isManagementOrAdministrator = useSelector((state: RootState) => getIsManagementOrAdministrator(state))
  const isProjectLeader = useSelector((state: RootState) => getIsProjectLeader(state))
  const canAddProject = isProjectLeader || isManagementOrAdministrator

  const [projectCount, setProjectCount] = useState<number>(0)
  const [projects, setProjects] = useState<Project[]>([])
  const [expandedTile, setExpandedTile] = useState<string | null>(null)

  const handleToggleCollapse = (id: string) => {
    setExpandedTile(prev => (prev === id ? null : id))
  }

  const updateSelectedData = useCallback(
    (selectedIds: ReportPopupData[]) => {
      setSelectedData(selectedIds)
    },
    [setSelectedData]
  )

  const statusTranslationMapping = {
    [ProjectStatus.ACTIVE]: 'page.projects.filterPopup.active',
    [ProjectStatus.INACTIVE]: 'page.projects.filterPopup.inactive',
    [ProjectStatus.FINISHED]: 'page.projects.filterPopup.finished',
    [ProjectStatus.DELETED]: 'page.projects.filterPopup.deleted',
    [ProjectStatus.TRIAL]: 'page.projects.filterPopup.trial',
    [ProjectStatus.NONE_SELECTED]: 'page.projects.filterPopup.noneSelected',
  }

  const handleSearch = (value: string) => {
    setRequestProps({ ...requestProps, 'query:name,acronym': value, page: 0 })
    setPage(0)
  }

  const setProjectFavorite = async (projectId: string, newIsFavorite: boolean) => {
    await setIsProjectFavoriteForUser({
      projectId: projectId,
      isFavorite: newIsFavorite,
    })
  }

  useEffect(() => {
    dispatch(setPageName(t('page.name.projects')))
    dispatch(setPageIndex(PageIndex.PROJECTS))

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  const columns: Column[] = [
    { id: 'acronym', label: t('components.table.header.acronym') },
    { id: 'name', label: t('components.table.header.projectName') },
    { id: 'description', label: t('components.table.header.description') },
    { id: 'projectStatus', label: t('components.table.header.status') },
    { id: 'leaderLastNames', label: t('page.projects.filterPopup.leader') },
    { id: 'parent.acronym', label: t('page.projects.filterPopup.parent') },
    { id: 'favorite', label: t('components.table.header.favorite') },
    { id: 'empty', label: t('') },
    { id: 'action', label: t('components.table.header.action') },
  ]

  const { data: projectsResponse, isLoading, isSuccess } = useGetProjectsQuery(requestProps)

  const {
    data: activeProjectsResponse,
    isLoading: isActiveLoading,
    isSuccess: isActiveSuccess,
  } = useGetProjectsQuery({
    ...defaultProps,
    projectStatus: 'ACTIVE',
  })

  useEffect(() => {
    if (projectsResponse && isSuccess) {
      setProjects(projectsResponse.content)
    }
  }, [isSuccess, projectsResponse])

  useEffect(() => {
    if (activeProjectsResponse && isActiveSuccess) {
      setProjectCount(activeProjectsResponse.totalElements)
    }
  }, [activeProjectsResponse, isActiveSuccess])

  const extractData = (row: Row): Project => ({ ...row } as Project)

  const renderProjectTile = (project: Row, _labelId: string, checkbox?: React.ReactNode) => {
    const projectData = extractData(project)
    const id = project.id

    return (
      <Box justifyContent="space-between" alignItems="center">
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1} marginBottom="5px">
            <Stack direction="row" alignItems="center">
              {checkbox}
              <Box
                sx={{
                  height: 'fit-content',
                  border: '1px solid',
                  borderRadius: 1,
                  p: '5px',
                  width: 'fit-content',
                  borderColor: 'background.elevation8',
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {t(statusTranslationMapping[project.projectStatus as keyof typeof statusTranslationMapping])}
                </Typography>
              </Box>
            </Stack>
            <Box display="inline-flex" alignItems="center">
              <IconButton onClick={() => setProjectFavorite(project.id, !project.favorite)}>
                {project.favorite ? (
                  <SvgIcon>
                    <Star />
                  </SvgIcon>
                ) : (
                  <SvgIcon>
                    <StarOutline />
                  </SvgIcon>
                )}
              </IconButton>
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {project.privacy && (
              <SvgIcon titleAccess={t('components.tooltip.privateProject')}>
                <LockOutlined />
              </SvgIcon>
            )}
            <Box>
              <ProjectActionMenu isInTile={true} project={projectData} />
            </Box>
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="space-between" marginLeft="2px">
          <Stack
            direction="row"
            justifyContent="space-between"
            spacing={1}
            onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}
            sx={{ cursor: 'pointer' }}
          >
            <AvatarComponent
              avatar={projectData.avatar}
              extension={projectData.extension}
              alt={`${projectData.name}-project-avatar`}
              size={36}
            />
            <Stack direction="column" justifyContent="space-between">
              <Typography variant="h6">{project.acronym}</Typography>
              <Typography variant="subtitle1" component="div" sx={{ cursor: 'pointer' }}>
                {project.name}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="column" justifyContent="space-between" marginTop="8px">
          {projectData.leaders && projectData.leaders.length > 1 && (
            <Box>
              <Typography variant="body2" color="textSecondary" fontWeight={580}>
                {t('page.users.filtering.leaders')}:
              </Typography>
              <List sx={{ py: '2px' }}>
                {projectData.leaders
                  .slice()
                  .sort((a, b) => (b.main ? 1 : -1))
                  .map((leader, index) => (
                    <ListItem key={index} sx={{ py: '4px' }}>
                      <Typography variant="body2">- {`${leader.firstName} ${leader.lastName}`}</Typography>
                    </ListItem>
                  ))}
              </List>
            </Box>
          )}
          {projectData.leaders && projectData.leaders.length === 1 && (
            <Box>
              <Stack direction="row" spacing="5px">
                <Typography variant="body2" color="textSecondary" fontWeight={580}>
                  {t('page.users.filtering.leader')}:
                </Typography>
                <Typography variant="body2">
                  {projectData.leaders.map((leader: FullName) => `${leader.firstName} ${leader.lastName}`)}
                </Typography>
              </Stack>
            </Box>
          )}
          {project.parent && (
            <Box>
              <Stack direction="row" spacing="5px" marginY="10px">
                <Typography variant="body2" color="textSecondary" fontWeight={580}>
                  {t('page.projects.filterPopup.parent')}:
                </Typography>
                <Typography variant="body2">{project.parent?.acronym || '-'}</Typography>
              </Stack>
            </Box>
          )}
        </Stack>
        {project.description && (
          <Stack direction="column" justifyContent="space-between" marginTop="8px">
            {project.description.length > 36 ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    {!(expandedTile === id) && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          cursor: 'pointer',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {project.description}
                      </Typography>
                    )}
                  </Box>
                  <IconButton size="small" onClick={() => handleToggleCollapse(id)}>
                    {expandedTile === id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Stack>
                <Collapse in={expandedTile === id} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      mt: 1,
                      pl: 2,
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    <Typography variant="body2">{project.description}</Typography>
                  </Box>
                </Collapse>
              </>
            ) : (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                }}
              >
                {project.description}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    )
  }

  const renderRow = (project: Row, labelId: string) => {
    const id = project.id
    const projectData = extractData(project)

    return (
      <>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AvatarComponent
              avatar={projectData.avatar}
              extension={projectData.extension}
              alt={projectData.name + '-project-avatar'}
              size={32}
            />
            <Box sx={{ mt: 1, mb: 1 }} display={'inline-flex'} alignItems={'center'}>
              {project.acronym}
            </Box>
          </Stack>
        </TableCell>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))} id={labelId}>
          {project.name}
        </TableCell>
        <TableCell
          onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}
          sx={{
            maxWidth: '228px',
          }}
        >
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <Tooltip title={project.description}>
              <Typography variant="body2">{project.description}</Typography>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}>
          {t(statusTranslationMapping[project.projectStatus as keyof typeof statusTranslationMapping])}
        </TableCell>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}>
          {projectData.leaders
            ? projectData.leaders
                .slice()
                .sort((a, b) => (b.main ? 1 : -1))
                .map((leader, index) => (
                  <span key={leader.id}>
                    {leader.main ? (
                      <strong>{`${leader.firstName} ${leader.lastName}`}</strong>
                    ) : (
                      `${leader.firstName} ${leader.lastName}`
                    )}
                    {index < projectData.leaders.length - 1 && ', '}
                  </span>
                ))
            : '-'}
        </TableCell>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}>
          {project.parent?.acronym || '-'}
        </TableCell>
        <TableCell>
          <Box display={'inline-flex'} alignItems={'center'}>
            <IconButton
              onClick={() => setProjectFavorite(project.id, !project.favorite)}
              aria-label={`${project.name}-favorite-${project.favorite}`}
            >
              {project.favorite ? (
                <SvgIcon>
                  <Star />
                </SvgIcon>
              ) : (
                <SvgIcon>
                  <StarOutline />
                </SvgIcon>
              )}
            </IconButton>
          </Box>
        </TableCell>
        <TableCell onClick={() => navigate(generatePath(navigation.projectDetails, { id }))}>
          {project.privacy ? (
            <Box sx={{ mt: 1, mb: 1 }} display={'inline-flex'} alignItems={'center'}>
              <Tooltip title={t('components.tooltip.privateProject')}>
                <SvgIcon>
                  <LockOutlined />
                </SvgIcon>
              </Tooltip>
            </Box>
          ) : null}
        </TableCell>
        <StickyCell
          align="center"
          sx={{
            borderLeft: '2px solid',
            borderLeftColor: 'background.elevation8',
            right: 0,
            '&:hover': {
              cursor: 'auto',
            },
          }}
        >
          <ProjectActionMenu project={projectData} />
        </StickyCell>
      </>
    )
  }
  return (
    <StyledPageContent display={'flex'} flexDirection={'column'} gap={'24px'} color="text.light">
      <Box display={'flex'} flexDirection={'row'} gap={'24px'} justifyContent={'space-between'} alignItems={'center'}>
        <HeaderCountLabel label={t('components.subheader.activeProjects')} count={projectCount} />
        {canAddProject && isDownLg && !isDownSm && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            size="medium"
            onClick={() => {
              navigate(navigation.newProject)
            }}
          >
            <Add />
            {t('page.projects.addProject')}
          </Button>
        )}
        {canAddProject && isDownSm && (
          <SquareButton
            color="primary"
            size="medium"
            onClick={() => {
              navigate(navigation.newProject)
            }}
          >
            <Add />
          </SquareButton>
        )}
      </Box>

      <Box
        display={'flex'}
        flexDirection={isDownMd ? 'column-reverse' : 'row'}
        width={'100%'}
        gap={'16px'}
        alignItems={'center'}
      >
        {!isDownMd && (
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            startIcon={<FilterList />}
            size="medium"
            className={isFilterMenuOpen ? 'selected' : ''}
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
          >
            {t('page.usersProfile.filterTags')}
          </Button>
        )}

        {!isDownSm && isDownMd && (
          <Box
            display={'flex'}
            flexDirection={'row'}
            width={'100%'}
            gap={'16px'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Button
              variant="contained"
              color="secondary"
              disableElevation
              startIcon={<FilterList />}
              size="medium"
              className={isFilterMenuOpen ? 'selected' : ''}
              onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            >
              {t('page.usersProfile.filterTags')}
            </Button>

            <GenerateReportPopup
              selectedData={selectedData}
              reportType={ReportType.Projects}
              leaderId={currentUser?.id}
            />
          </Box>
        )}

        {isDownSm && (
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            startIcon={<FilterList />}
            size="medium"
            className={isFilterMenuOpen ? 'selected' : ''}
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            fullWidth={isDownSm}
          >
            {t('page.usersProfile.filterTags')}
          </Button>
        )}

        {isDownSm && (
          <GenerateReportPopup
            selectedData={selectedData}
            reportType={ReportType.Projects}
            leaderId={currentUser?.id}
          />
        )}

        <Searchbar placeholder={t('components.placeholder.search')} handleChange={handleSearch} />

        {!isDownMd && (
          <span>
            <GenerateReportPopup
              selectedData={selectedData}
              reportType={ReportType.Projects}
              leaderId={currentUser?.id}
            />
          </span>
        )}

        {canAddProject && !isDownLg && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            size="medium"
            onClick={() => {
              navigate(navigation.newProject)
            }}
          >
            <Add />
            {t('page.projects.addProject')}
          </Button>
        )}
      </Box>

      <Box
        display={'flex'}
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={'space-between'}
        gap={isFilterMenuOpen ? 2 : 0}
        height="fit-content"
        pb={3}
      >
        {isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation="vertical">
            <FilterProjectsMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        {!isDownMd && (
          <Collapse in={isFilterMenuOpen} orientation="horizontal">
            <FilterProjectsMenu requestProps={requestProps} setRequestProps={setRequestProps} setPage={setPage} />
          </Collapse>
        )}
        <DataTable
          renderTileCheckbox={true}
          singleSelection={false}
          updateSelectedData={updateSelectedData}
          columns={columns}
          data={projects}
          renderRow={(project: Row, labelId: string) => renderRow(project, labelId) as React.ReactNode}
          totalElements={projectsResponse?.totalElements || 0}
          setRequestProps={setRequestProps}
          translationForNotFound={'page.worklog.form.projectsNotFound'}
          page={page}
          setPage={setPage}
          isLoading={isLoading && isActiveLoading}
          unsortableColumns={[
            { id: 'favorite', label: t('components.table.header.favorite') },
            { id: 'empty', label: t('') },
            { id: 'action', label: t('components.table.header.action') },
          ]}
          commonRowsPerPage={
            localStorage.getItem(LocalStorageTypes.ProjectTableRows)
              ? parseInt(localStorage.getItem(LocalStorageTypes.ProjectTableRows)!)
              : undefined
          }
          localStorageRowsName={LocalStorageTypes.ProjectTableRows}
          renderTileContent={(project: Row, labelId: string, checkbox?: React.ReactNode) =>
            renderProjectTile(project, labelId, checkbox) as React.ReactNode
          }
        />
      </Box>
    </StyledPageContent>
  )
}

export default ProjectsView
